'use client';

import { H2 } from '@components/text';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import placeholderImage from 'assets/images/placeholder.png';
import useImage from 'hooks/use-image';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { validateString } from 'utils/validate-string';

import styles from './article-card.module.scss';

interface CardProps {
  readonly link: string;
  readonly title: string;
  readonly description?: string | undefined;
  readonly media?: SanityImageSource | undefined;
  readonly footer?: ReactNode;
}

const rootClass = validateString(styles.root);
const contentClass = validateString(styles.content);
const titleClass = validateString(styles.title);
const footerClass = validateString(styles.footer);
const imageClass = validateString(styles.image);
const imageWrapperClass = validateString(styles.imageWrapper);

function ArticleCard({
  link,
  title,
  description,
  media,
  footer,
}: CardProps): ReactElement {
  const imageProps = useImage(media ?? null);

  return (
    <article>
      <Link className={rootClass} href={link}>
        <header className={imageWrapperClass}>
          {imageProps !== null ? (
            <Image
              alt={title}
              className={imageClass}
              fill
              loader={imageProps.loader}
              src={imageProps.src}
              style={{ objectPosition: imageProps.objectPosition }}
              {...(typeof imageProps.blurData !== 'undefined' && {
                placeholder: 'blur',
                blurDataURL: imageProps.blurData,
              })}
            />
          ) : (
            <Image
              alt={title}
              className={imageClass}
              fill
              src={placeholderImage}
            />
          )}
        </header>
        <section className={contentClass}>
          <H2 className={titleClass}>{title}</H2>
          <p>{description}</p>
        </section>
        <footer className={footerClass}>{footer}</footer>
      </Link>
    </article>
  );
}

export default ArticleCard;
